import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';
import { EmployeeSelectorService } from '../../../../data-services/employee-selector-service';

@Injectable({ providedIn: 'root' })
export class AskAnEmployeeService extends AutomationDisplayService {
  constructor(
    private readonly employeeSelectorService: EmployeeSelectorService,
    protected readonly translateService: TranslateService,
  ) {
    super();
  }

  getActivityColumn(_namespace: string, activity: ActivityInterface): ActivityTableEntry {
    const data = activity?.workflowStep?.data ? JSON.parse(activity?.workflowStep?.data) : {};
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
    return this.tableEntry(
      this.employeeSelectorService.get(data.employee_id).pipe(
        switchMap((info) =>
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASK_AN_EMPLOYEE.ACTIVITY', {
            name: info.name,
          }),
        ),
      ),
      of(activityData?.answer || ''),
    );
  }

  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    const data = step ? JSON.parse(step.data) : {};

    // If there's a list, we're happy.
    if (data.employee_id) {
      return this.nodeDisplayData(
        // Insert the name of the list into nicer text.
        this.employeeSelectorService.get(data.employee_id).pipe(
          switchMap((employee) => {
            if (data.prompt_name) {
              return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASK_AN_EMPLOYEE.ACTION_WITH_PROMPT_NAME', {
                name: employee.name,
                promptName: data.prompt_name,
              });
            }
            return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASK_AN_EMPLOYEE.ACTION', {
              name: employee.name,
            });
          }),
        ),
        this.noValidationErrorsDetected(),
      );
    }

    // If not, we need placeholder data.
    return this.nodeDisplayData(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASK_AN_EMPLOYEE.TITLE'),
      this.nonDismissibleValidationErrorsDetected(
        step,
        this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.ASK_AN_EMPLOYEE.CONFIG_ERROR'),
      ),
    );
  }

  getTemplateNodeData(
    _namespace: string,
    _step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
  ): Observable<NodeData> {
    return this.nodeDisplayData(
      this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ASK_AN_EMPLOYEE.TITLE'),
      this.noValidationErrorsDetected(),
    );
  }
}
